import React, { useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { SxProps, Theme } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import { intakeFieldSlot } from './intakeFormLayout';

type Props = {
  /** YYYY-MM-DD 文字列（未入力は ''） */
  value: string;
  /** YYYY-MM-DD 文字列を返す（クリア時は ''） */
  onChange: (value: string) => void;
  required?: boolean;
  helperText?: React.ReactNode;
  /** 入力欄の id（必須チェックのフォーカス移動などに使用） */
  id?: string;
  /**
   * 生年月日モード。年の選択から開始し、未来日を選べないようにする。
   * 手入力も可能（遠い年は手打ちが速いため）。
   */
  dob?: boolean;
  /** クリア操作を許可するか（依頼日など空にできない日付は false） */
  clearable?: boolean;
  disabled?: boolean;
  fieldSx?: SxProps<Theme>;
};

/**
 * 受付フォーム用の日付入力。
 * - カレンダーアイコンだけでなく、入力欄本体をクリックしてもカレンダーが開く
 *   （初診日・同意書アポ日など近日の日付を素早く選べるようにするため）
 * - 直接キー入力も可能
 * 親コンポーネントの LocalizationProvider 配下で使用すること。
 */
const IntakeDatePicker: React.FC<Props> = ({
  value,
  onChange,
  required,
  helperText,
  id,
  dob,
  clearable = true,
  disabled,
  fieldSx,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <DatePicker
      disabled={disabled}
      value={value ? dayjs(value) : null}
      onChange={(v: Dayjs | null) => onChange(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
      format="YYYY/MM/DD"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      openTo={dob ? 'year' : 'day'}
      views={dob ? ['year', 'month', 'day'] : undefined}
      disableFuture={dob}
      minDate={dob ? dayjs('1900-01-01') : undefined}
      maxDate={dob ? dayjs() : undefined}
      slotProps={{
        textField: {
          ...intakeFieldSlot,
          sx: [intakeFieldSlot.sx, ...(Array.isArray(fieldSx) ? fieldSx : fieldSx ? [fieldSx] : [])],
          required,
          helperText,
          id,
          // 入力欄のどこをクリックしてもカレンダーを開く。
          // 開いている間のカレンダーアイコン(閉じる)操作を打ち消さないよう「閉→開」のみ反応させる。
          onClick: () => {
            if (!open) setOpen(true);
          },
        },
        actionBar: { actions: clearable ? ['today', 'clear'] : ['today'] },
      }}
    />
  );
};

export default IntakeDatePicker;
